import * as Sentry from '@sentry/vue'

interface MemoryPerformance {
  usedJSHeapSize: number
  totalJSHeapSize: number
  jsHeapSizeLimit: number
}

interface PerformanceWithMemory extends Performance {
  memory: MemoryPerformance
}

interface UseSPAErrorHandlingReturn {
  monitorMemoryLeaks: () => void
  handleChunkError: (error: Error) => boolean
}

export const useSPAErrorHandling = (): UseSPAErrorHandlingReturn => {
  const monitorMemoryLeaks = (): void => {
    if (!('memory' in performance)) return

    const measurements: number[] = []
    const checkInterval = 30000 // 30s
    const measurementWindow = 10

    setInterval(() => {
      const memory = (performance as PerformanceWithMemory).memory
      const usage = memory.usedJSHeapSize / memory.jsHeapSizeLimit

      measurements.push(usage)
      if (measurements.length > measurementWindow) {
        measurements.shift()
      }

      if (measurements.length === measurementWindow) {
        const isGrowing = measurements.every((val, idx) => idx === 0 || val > measurements[idx - 1])

        if (isGrowing && usage > 0.7) {
          Sentry.captureMessage('Potential memory leak detected', {
            level: 'warning',
            contexts: {
              memory: {
                current: memory.usedJSHeapSize,
                limit: memory.jsHeapSizeLimit,
                trend: 'increasing',
                measurements
              }
            }
          })
        }
      }
    }, checkInterval)
  }

  const handleChunkError = (error: Error): boolean => {
    if (error.message?.includes('Loading chunk')) {
      Sentry.captureMessage('Chunk loading failed', {
        level: 'warning',
        tags: {
          'error.type': 'chunk_load'
        }
      })

      if (confirm('A new version is available. Reload to update?')) {
        window.location.reload()
      }

      return true
    }
    return false
  }

  return {
    monitorMemoryLeaks,
    handleChunkError
  }
}
